import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';

const questions = [
  { text: 'Você sente que sua vida financeira é uma montanha-russa?', element: 'terra', icon: '🌱', color: 'green' },
  { text: 'Você se preocupa frequentemente com o que pode acontecer no futuro?', element: 'terra', icon: '🌱', color: 'green' },
  { text: 'Você se sente sobrecarregado com a quantidade de coisas a fazer?', element: 'terra', icon: '🌱', color: 'green' },
  { text: 'Você tem dificuldade em relaxar porque está sempre pensando?', element: 'agua', icon: '🌊', color: 'blue' },
  { text: 'Você frequentemente duvida das suas próprias capacidades?', element: 'agua', icon: '🌊', color: 'blue' },
  { text: 'Você se compara frequentemente a outras pessoas?', element: 'agua', icon: '🌊', color: 'blue' },
  { text: 'Você se distrai facilmente ao tentar trabalhar?', element: 'fogo', icon: '🔥', color: 'orange' },
  { text: 'Você frequentemente se sente sem motivação?', element: 'fogo', icon: '🔥', color: 'orange' },
  { text: 'Você sente que não é bom o suficiente?', element: 'fogo', icon: '🔥', color: 'orange' },
  { text: 'Você se sente frequentemente sobrecarregado e estressado?', element: 'ar', icon: '🌪️', color: 'purple' },
  { text: 'Você tem medo constante de fracassar?', element: 'ar', icon: '🌪️', color: 'purple' },
  { text: 'Você tem dificuldade em aceitar mudanças?', element: 'ar', icon: '🌪️', color: 'purple' }
];

const getElementColors = (color: string) => {
  const colors = {
    green: { border: 'border-green-700', bg: 'bg-green-50', text: 'text-green-800', icon: 'text-green-700' },
    blue: { border: 'border-blue-800', bg: 'bg-blue-50', text: 'text-blue-900', icon: 'text-blue-800' },
    orange: { border: 'border-orange-600', bg: 'bg-orange-50', text: 'text-orange-800', icon: 'text-orange-700' },
    purple: { border: 'border-purple-600', bg: 'bg-purple-50', text: 'text-purple-800', icon: 'text-purple-700' }
  };
  return colors[color as keyof typeof colors] || colors.purple;
};

export default function Quiz() {
  const navigate = useNavigate();
  const {
    updateScore,
    calculateResult,
    isSubmitted,
    setInteresseLeituraVibracional,
    setInteresseProtocoloLiberacao
  } = useQuiz();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extraStep, setExtraStep] = useState<'protocolo' | 'leitura' | null>(null);
  const [protocoloAnswer, setProtocoloAnswer] = useState<string | null>(null);
  const [vibrationalAnswer, setVibrationalAnswer] = useState<string | null>(null);

  const totalSteps = questions.length + 2;
  const displayedQuestion = extraStep === 'protocolo' ? 13 : extraStep === 'leitura' ? 14 : currentQuestion + 1;
  const progress = extraStep
    ? (displayedQuestion / totalSteps) * 100
    : ((currentQuestion + 1) / totalSteps) * 100;
  const question = questions[currentQuestion];
  const extraSelected = extraStep === 'protocolo' ? protocoloAnswer : vibrationalAnswer;

  const handleAnswer = async (answer: boolean) => {
    // Verificar se já foi enviado
    if (isSubmitted) {
      alert('Suas respostas já foram enviadas com sucesso. Obrigada.');
      navigate('/resultado');
      return;
    }

    setSelectedAnswer(answer);
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        updateScore(question.element as any, answer ? 1 : 0);
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      }, 400);
    } else {
      // Última pergunta dos elementos - atualizar score e mostrar pergunta 13
      setIsProcessing(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateScore(question.element as any, answer ? 1 : 0);
      setSelectedAnswer(null);
      setIsProcessing(false);
      setExtraStep('protocolo');
    }
  };

  const isLastQuestion = currentQuestion === questions.length - 1

  const handleExtraAnswer = async (answer: string) => {
    if (extraStep === 'protocolo') {
      setProtocoloAnswer(answer);
      setInteresseProtocoloLiberacao(answer);
      setIsProcessing(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsProcessing(false);
      setExtraStep('leitura');
      return;
    }

    setVibrationalAnswer(answer);
    setInteresseLeituraVibracional(answer);
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    await calculateResult(undefined, undefined, answer, protocoloAnswer || undefined);
    navigate('/resultado');
  };

  return (
    <div className="reative-page min-h-screen" style={{ backgroundColor: '#0d0d0f' }}>
      {/* Logo */}
      <div className="pt-8 pb-4 text-center px-4">
        <p className="font-bold text-amber-400 italic">Reacenda Luz</p>
      </div>

      {/* Barra de Progresso */}
      <div className="max-w-3xl mx-auto px-4 mb-8">
        <div className="rounded-full h-4 overflow-hidden border border-gray-700" style={{ backgroundColor: '#1c1c21' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-gradient-to-r from-amber-500 to-purple-600 rounded-full"
          />
        </div>
        <div className="flex justify-between mt-3 font-semibold text-gray-400">
          <span>Pergunta <span className="numerals">{displayedQuestion}</span> de <span className="numerals">{totalSteps}</span></span>
          <span><span className="numerals">{Math.round(progress)}%</span> completo</span>
        </div>
      </div>

      {/* Conteúdo da Pergunta */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <AnimatePresence mode="wait">
          {!extraStep ? (
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4 }}
            className="rounded-3xl shadow-2xl p-6 md:p-12 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
            
            {/* Ícones dos 4 Elementos */}
            <div className="flex justify-center gap-3 mb-8">
              <span className="text-2xl">🌱</span>
              <span className="text-2xl">🌊</span>
              <span className="text-2xl">🔥</span>
              <span className="text-2xl">🌪️</span>
            </div>

            {/* Texto da Pergunta */}
            <h2 className="reative-instruction font-bold text-white text-center mb-8 md:mb-12 leading-relaxed">
              {question.text}
            </h2>

            {/* Cards de Resposta */}
            <div className="reative-answer-card grid grid-cols-2 gap-4 md:gap-6">
              {/* Card SIM */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleAnswer(true)}
                disabled={isProcessing}
                className={`rounded-2xl border-2 font-bold transition-all duration-300 shadow-lg ${
                  selectedAnswer === true
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white border-amber-600 shadow-xl'
                    : 'border-gray-600 text-gray-200 hover:border-amber-500 hover:bg-amber-900/20'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ backgroundColor: selectedAnswer === true ? undefined : '#1c1c21', minHeight: '52px', paddingTop: '16px', paddingBottom: '16px' }}>
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl md:text-3xl mb-2">✓</div>
                    SIM
                  </>
                )}
              </motion.button>

              {/* Card NÃO */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleAnswer(false)}
                disabled={isProcessing}
                className={`rounded-2xl border-2 font-bold transition-all duration-300 shadow-lg ${
                  selectedAnswer === false
                    ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white border-purple-700 shadow-xl'
                    : 'border-gray-600 text-gray-200 hover:border-purple-500 hover:bg-purple-900/20'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ backgroundColor: selectedAnswer === false ? undefined : '#1c1c21', minHeight: '52px', paddingTop: '16px', paddingBottom: '16px' }}>
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl md:text-3xl mb-2">✗</div>
                    NÃO
                  </>
                )}
              </motion.button>
            </div>

            {/* Texto do Botão */}
            <div className="text-center mt-6 md:mt-8 text-gray-500">
              {isProcessing ? (
                <span className="text-purple-400 font-semibold">Processando suas respostas...</span>
              ) : isLastQuestion ? (
                'Última pergunta! Clique para descobrir seu resultado.'
              ) : (
                'Selecione sua resposta para continuar'
              )}
            </div>
          </motion.div>
          ) : (
          <motion.div
            key={extraStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl shadow-2xl p-6 md:p-12 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
            
            {/* Ícones dos 4 Elementos */}
            <div className="flex justify-center gap-3 mb-8">
              <span className="text-2xl">🌱</span>
              <span className="text-2xl">🌊</span>
              <span className="text-2xl">🔥</span>
              <span className="text-2xl">🌪️</span>
            </div>

            {/* Texto da Pergunta Adicional */}
            <div className="mb-8 md:mb-12">
              <p className="text-gray-300 text-center leading-relaxed mb-6">
                {extraStep === 'protocolo' ? (
                  <>
                    Existe um nível de transformação que vai muito além deste teste. Através do <strong>Protocolo de Liberação e Cocriação</strong> eu crio uma sessão de liberação gravada em áudio exclusivamente para você, para ouvir enquanto dorme. É um processo profundo para liberar o que te prende e reprogramar sua vida de dentro para fora.
                  </>
                ) : (
                  <>
                    Existe também um nível de investigação que vai muito além deste teste. Através da <strong>Leitura do Campo Vibracional</strong> eu capto as informações diretamente do seu campo energético e revelo padrões invisíveis que nem você mesmo consegue ver, na prosperidade, nos relacionamentos, nas emoções e no propósito.
                  </>
                )}
              </p>
              <h3 className="font-bold text-white text-center mb-8">
                Você gostaria de receber mais informações sobre isso no seu WhatsApp?
              </h3>
            </div>

            {/* Botões de Resposta */}
            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleExtraAnswer('Sim, quero saber mais')}
                disabled={isProcessing}
                className={`w-full rounded-2xl border-2 font-bold transition-all duration-300 shadow-lg ${
                  extraSelected === 'Sim, quero saber mais'
                    ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white border-amber-700 shadow-xl'
                    : isProcessing
                    ? 'border-gray-700 text-gray-500 cursor-not-allowed'
                    : 'text-amber-400 border-amber-500 hover:bg-amber-900/20'
                }`}
                style={{ backgroundColor: extraSelected === 'Sim, quero saber mais' || isProcessing ? undefined : '#1c1c21', minHeight: '52px', padding: '16px' }}>
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                    <span className="ml-3">Processando...</span>
                  </div>
                ) : (
                  '✓ Sim, quero saber mais'
                )}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleExtraAnswer('Não, por enquanto não')}
                disabled={isProcessing}
                className={`w-full rounded-2xl border-2 font-bold transition-all duration-300 shadow-lg ${
                  extraSelected === 'Não, por enquanto não'
                    ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white border-purple-700 shadow-xl'
                    : isProcessing
                    ? 'border-gray-700 text-gray-500 cursor-not-allowed'
                    : 'text-purple-400 border-purple-500 hover:bg-purple-900/20'
                }`}
                style={{ backgroundColor: extraSelected === 'Não, por enquanto não' || isProcessing ? undefined : '#1c1c21', minHeight: '52px', padding: '16px' }}>
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-600 border-t-transparent"></div>
                    <span className="ml-3">Processando...</span>
                  </div>
                ) : (
                  '✗ Não, por enquanto não'
                )}
              </motion.button>
            </div>

            {/* Texto de Segurança */}
            <div className="text-center mt-6 text-gray-500">
              {isProcessing ? (
                <span className="text-purple-400 font-semibold">{extraStep === 'leitura' ? 'Preparando seu resultado...' : 'Registrando sua resposta...'}</span>
              ) : (
                'Sua resposta será registrada com segurança'
              )}
            </div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Rodapé */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-gray-600 mt-8 px-4 reative-footer">
          <p className="mb-2">Seus dados estão seguros e não serão compartilhados.</p>
          <p className="font-semibold">Mônica Maria, Reacenda Luz</p>
        </motion.div>
      </div>
    </div>
  );
}
